import { useCheckAccountType } from '@cmg/auth';
import { FlexContainer } from '@cmg/common';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import ServerErrorsBanner from '../../../../common/components/indicators/server-error/ServerErrorsBanner';
import NotAccessible from '../../../../common/components/layout/not-accessible/NotAccessible';
import Spinner from '../../../../common/components/overlays/spinner/Spinner';
import { useConvertibleFilingTalkRanges } from '../../../../common/hooks/useConvertibleFilingTalkRanges';
import useCheckCoveredInstitutionalIndicationAccess from '../../../../common/util/check-access/useCheckCoveredInstitutionalIndicationAccess';
import { useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess } from '../../../../common/util/check-access/useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess';
import { useCmgEntityKeyContext } from '../../contexts/CmgEntityKeyContext';
import { useOfferingSidePanelOfferingId } from '../../hooks/useOfferingSidePanelMatch';
import BuySideUpdateHistory from './components/indication-update-history/BuySideUpdateHistory';
import CoveredIndicationUpdateHistory from './components/indication-update-history/CoveredIndicationUpdateHistory';
import IndicationUpdateHistory from './components/indication-update-history/IndicationUpdateHistory';
import {
  useUpdateHistory_BasicPublishedOfferingQuery,
  useUpdateHistory_CoveredIndicationQuery,
  useUpdateHistory_IndicationQuery,
} from './graphql';
import { HistoryPanel } from './HistoryPanel';

const EMPTY_ARRAY = [];

const UpdateHistoryRoute: React.FC = () => {
  const { cmgEntityKey } = useCmgEntityKeyContext();
  const isBuySide = useCheckAccountType('BUY_SIDE');
  const offeringId = useOfferingSidePanelOfferingId();

  const { canRead: canReadIndication, loading: indicationAccessLoading } =
    useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess({ offeringId });

  const { canRead: canReadCoveredIndication } = useCheckCoveredInstitutionalIndicationAccess();

  const {
    data: offeringData,
    loading: offeringLoading,
    error: offeringError,
  } = useUpdateHistory_BasicPublishedOfferingQuery({ variables: { id: offeringId } });

  const { data, loading, error } = useUpdateHistory_IndicationQuery({
    variables: {
      offeringId,
      cmgEntityKey,
    },
    skip: canReadCoveredIndication || !cmgEntityKey || !canReadIndication,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: coveredIndicationsQueryData,
    error: coveredIndicationsQueryError,
    loading: coveredIndicationsQueryLoading,
  } = useUpdateHistory_CoveredIndicationQuery({
    variables: { offeringId, cmgEntityKey: cmgEntityKey! },
    skip: !canReadCoveredIndication || !cmgEntityKey,
    fetchPolicy: 'cache-and-network',
  });

  const indicationId =
    data?.institutionalIndication?.id ||
    coveredIndicationsQueryData?.coveredIndicationByCmgEntityKey?.id;

  const [offering] = defaultTo(offeringData?.publishedOfferings?.items, EMPTY_ARRAY);

  const convertibleTalkRanges = useConvertibleFilingTalkRanges(offering?.terms.filings);

  if (!indicationAccessLoading && !canReadIndication) {
    return (
      <HistoryPanel>
        <NotAccessible subject="Update History" />
      </HistoryPanel>
    );
  }

  if (indicationAccessLoading || loading || coveredIndicationsQueryLoading || offeringLoading) {
    return <Spinner show={true} fullHeight={true} />;
  }

  const bannerError = error || offeringError;
  if (bannerError) {
    return (
      <HistoryPanel>
        <ServerErrorsBanner error={bannerError} />
      </HistoryPanel>
    );
  }

  if (coveredIndicationsQueryError) {
    return (
      <HistoryPanel>
        <ServerErrorsBanner error={coveredIndicationsQueryError} />
      </HistoryPanel>
    );
  }

  if (canReadCoveredIndication) {
    return (
      <FlexContainer direction="column">
        <HistoryPanel>
          {indicationId && (
            <CoveredIndicationUpdateHistory
              offeringId={offeringId}
              offeringType={defaultTo(offering?.type, null)}
              issuerName={defaultTo(offering?.issuer.name, null)}
              indicationId={indicationId}
              pricingCurrencyCode={offering?.pricingCurrencyCode}
              securityTypeDisplayName={defaultTo(offering?.security.typeDisplayName, null)}
              convertibleTalkRanges={convertibleTalkRanges}
              isIndicationPriceAsPercentageParEnabled={defaultTo(
                offering.orderBookConfiguration?.isIndicationPriceAsPercentageParEnabled,
                null
              )}
            />
          )}
        </HistoryPanel>
      </FlexContainer>
    );
  }

  if (isBuySide) {
    return (
      <FlexContainer direction="column">
        <HistoryPanel>
          <BuySideUpdateHistory
            offeringId={offeringId}
            offeringType={defaultTo(offering?.type, null)}
            issuerName={defaultTo(offering?.issuer.name, null)}
            pricingCurrencyCode={offering?.pricingCurrencyCode}
            securityTypeDisplayName={defaultTo(offering?.security.typeDisplayName, null)}
            convertibleTalkRanges={convertibleTalkRanges}
            isIndicationPriceAsPercentageParEnabled={defaultTo(
              offering.orderBookConfiguration?.isIndicationPriceAsPercentageParEnabled,
              null
            )}
          />
        </HistoryPanel>
      </FlexContainer>
    );
  }

  return (
    <FlexContainer direction="column">
      <HistoryPanel>
        {indicationId && (
          <IndicationUpdateHistory
            offeringId={offeringId}
            offeringType={defaultTo(offering?.type, null)}
            issuerName={defaultTo(offering?.issuer.name, null)}
            indicationId={indicationId}
            pricingCurrencyCode={offering?.pricingCurrencyCode}
            securityTypeDisplayName={defaultTo(offering?.security.typeDisplayName, null)}
            convertibleTalkRanges={convertibleTalkRanges}
            isIndicationPriceAsPercentageParEnabled={defaultTo(
              offering.orderBookConfiguration?.isIndicationPriceAsPercentageParEnabled,
              null
            )}
          />
        )}
      </HistoryPanel>
    </FlexContainer>
  );
};

export default UpdateHistoryRoute;
